import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
  ArrowLeft, Briefcase, MapPin, DollarSign, Clock, 
  Upload, Send, CheckCircle2, Users, Rocket, Heart,
  Search, Filter, X, Home, Building2, GraduationCap, Star
} from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    types: [],
    work_modes: [],
    experience_levels: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    department: "",
    type: "",
    work_mode: "",
    experience_level: ""
  });
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    portfolio_url: "",
    cover_letter: "",
    resume_url: ""
  });
  const [resumeFile, setResumeFile] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.department) params.append('department', filters.department);
      if (filters.type) params.append('type', filters.type);
      if (filters.work_mode) params.append('work_mode', filters.work_mode);
      if (filters.experience_level) params.append('experience_level', filters.experience_level);
      
      const response = await axios.get(`${API}/careers/jobs?${params.toString()}`);
      setJobs(response.data.jobs || []);
      setFilterOptions(response.data.filters || {
        departments: [],
        types: [],
        work_modes: [],
        experience_levels: []
      });
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      department: "",
      type: "",
      work_mode: "",
      experience_level: ""
    });
  };

  const hasActiveFilters = filters.department || filters.type || filters.work_mode || filters.experience_level;

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setJobDetailsOpen(true);
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setApplicationOpen(true);
    setSubmitted(false);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      linkedin_url: "",
      portfolio_url: "",
      cover_letter: "",
      resume_url: ""
    });
    setResumeFile(null);
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }

    setResumeFile(file);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API}/careers/apply/upload-resume`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setFormData(prev => ({ ...prev, resume_url: response.data.resume_url }));
      toast.success("Resume uploaded");
    } catch (error) {
      toast.error("Failed to upload resume");
      setResumeFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/careers/apply`, {
        job_id: selectedJob.job_id,
        ...formData
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "commission":
        return <Badge className="bg-green-100 text-green-800">Commission-Based</Badge>;
      case "internship":
        return <Badge className="bg-blue-100 text-blue-800">Internship</Badge>;
      case "full_time":
        return <Badge className="bg-purple-100 text-purple-800">Full-Time</Badge>;
      case "part_time":
        return <Badge className="bg-amber-100 text-amber-800">Part-Time</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getWorkModeBadge = (mode) => {
    switch (mode) {
      case "remote":
        return <Badge variant="outline" className="border-teal-500 text-teal-700"><Home className="h-3 w-3 mr-1" />Remote</Badge>;
      case "onsite":
        return <Badge variant="outline" className="border-orange-500 text-orange-700"><Building2 className="h-3 w-3 mr-1" />On-site</Badge>;
      case "hybrid":
        return <Badge variant="outline" className="border-indigo-500 text-indigo-700"><Building2 className="h-3 w-3 mr-1" />Hybrid</Badge>;
      default:
        return <Badge variant="outline">{mode}</Badge>;
    }
  };

  const getExperienceBadge = (level) => {
    const labels = {
      entry: "Entry Level",
      mid: "Mid Level",
      senior: "Senior",
      lead: "Lead",
      executive: "Executive"
    };
    return <Badge variant="secondary"><GraduationCap className="h-3 w-3 mr-1" />{labels[level] || level}</Badge>;
  };

  const formatSalary = (job) => {
    if (!job.show_salary || (!job.salary_min && !job.salary_max)) return null;
    
    const format = (amount) => {
      if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}k`;
      }
      return `$${amount}`;
    };
    
    const period = job.salary_period === "yearly" ? "/yr" : job.salary_period === "monthly" ? "/mo" : "/hr";
    
    if (job.salary_min && job.salary_max) {
      return `${format(job.salary_min)} - ${format(job.salary_max)}${period}`;
    } else if (job.salary_min) {
      return `From ${format(job.salary_min)}${period}`;
    } else {
      return `Up to ${format(job.salary_max)}${period}`;
    }
  };

  const perks = [
    {
      icon: Rocket,
      title: "High Growth",
      description: "Join a fast-growing startup with unlimited potential"
    },
    {
      icon: Users,
      title: "Great Team",
      description: "Work with passionate people who care about our mission"
    },
    {
      icon: Heart,
      title: "Impact",
      description: "Help renters protect their homes and families"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <NestIcon className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>therentpayer</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
            Join Our Team
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Help us protect renters across America. We're looking for passionate individuals 
            who want to make a difference.
          </p>
          <div className="flex justify-center gap-8 flex-wrap">
            {perks.map((perk, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <perk.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{perk.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Young Serif, serif' }}>
            Open Positions
          </h2>

          {/* Search and Filter Section */}
          <div className="max-w-4xl mx-auto mb-8 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs by title, department, or keywords..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 pr-10"
                data-testid="job-search-input"
              />
              {filters.search && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, search: "" }))}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1">Active</Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear all
                </Button>
              )}
            </div>

            {/* Filter Options */}
            {showFilters && (
              <Card className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Department</Label>
                    <Select
                      value={filters.department}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, department: value === "all" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="filter-department">
                        <SelectValue placeholder="All departments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All departments</SelectItem>
                        {filterOptions.departments.map(dept => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Job Type</Label>
                    <Select
                      value={filters.type}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, type: value === "all" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="filter-type">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        <SelectItem value="full_time">Full-Time</SelectItem>
                        <SelectItem value="part_time">Part-Time</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                        <SelectItem value="commission">Commission</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Work Mode</Label>
                    <Select
                      value={filters.work_mode}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, work_mode: value === "all" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="filter-work-mode">
                        <SelectValue placeholder="All modes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All modes</SelectItem>
                        <SelectItem value="remote">Remote</SelectItem>
                        <SelectItem value="onsite">On-site</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Experience</Label>
                    <Select
                      value={filters.experience_level}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, experience_level: value === "all" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="filter-experience">
                        <SelectValue placeholder="All levels" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All levels</SelectItem>
                        <SelectItem value="entry">Entry Level</SelectItem>
                        <SelectItem value="mid">Mid Level</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="executive">Executive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No open positions {hasActiveFilters ? "match your filters" : "at the moment"}. {hasActiveFilters ? "Try adjusting your filters." : "Check back soon!"}</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-6 max-w-4xl mx-auto">
              {jobs.map((job) => (
                <Card key={job.job_id} className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`job-${job.job_id}`} onClick={() => handleViewDetails(job)}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-xl">{job.title}</CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Briefcase className="h-4 w-4" />
                            {job.department}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {job.location}
                          </span>
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {getTypeBadge(job.type)}
                        {job.work_mode && getWorkModeBadge(job.work_mode)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4 line-clamp-2">
                      {job.description}
                    </p>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {job.experience_level && getExperienceBadge(job.experience_level)}
                        {formatSalary(job) && (
                          <Badge variant="outline" className="border-green-500 text-green-700">
                            <DollarSign className="h-3 w-3 mr-1" />
                            {formatSalary(job)}
                          </Badge>
                        )}
                      </div>
                      <Button onClick={(e) => { e.stopPropagation(); handleApply(job); }} data-testid={`apply-${job.job_id}`}>
                        Apply Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Job Details Dialog */}
      <Dialog open={jobDetailsOpen} onOpenChange={setJobDetailsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="text-2xl">{selectedJob.title}</DialogTitle>
                    <DialogDescription className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {selectedJob.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedJob.location}
                      </span>
                    </DialogDescription>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {getTypeBadge(selectedJob.type)}
                  {selectedJob.work_mode && getWorkModeBadge(selectedJob.work_mode)}
                  {selectedJob.experience_level && getExperienceBadge(selectedJob.experience_level)}
                  {formatSalary(selectedJob) && (
                    <Badge variant="outline" className="border-green-500 text-green-700">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {formatSalary(selectedJob)}
                    </Badge>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Description */}
                <div>
                  <h4 className="font-semibold mb-2">About this role</h4>
                  <p className="text-muted-foreground whitespace-pre-line">{selectedJob.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {selectedJob.responsibilities.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Requirements</h4>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {selectedJob.requirements.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preferred Qualifications */}
                {selectedJob.preferred_qualifications && selectedJob.preferred_qualifications.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      Nice to Have
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {selectedJob.preferred_qualifications.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      What We Offer
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {selectedJob.benefits.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setJobDetailsOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => { setJobDetailsOpen(false); handleApply(selectedJob); }} data-testid="apply-from-details-btn">
                  Apply for this Position
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Dialog */}
      <Dialog open={applicationOpen} onOpenChange={setApplicationOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <DialogTitle className="text-2xl mb-2">Application Submitted!</DialogTitle>
              <DialogDescription>
                Thank you for applying to {selectedJob?.title}. We'll review your application 
                and get back to you soon.
              </DialogDescription>
              <Button className="mt-6" onClick={() => setApplicationOpen(false)}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Apply for {selectedJob?.title}</DialogTitle>
                <DialogDescription>
                  {selectedJob?.department} • {selectedJob?.location}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn URL</Label>
                    <Input
                      id="linkedin"
                      type="url"
                      placeholder="https://linkedin.com/in/..."
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="portfolio">Portfolio URL</Label>
                    <Input
                      id="portfolio"
                      type="url"
                      placeholder="https://..."
                      value={formData.portfolio_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, portfolio_url: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resume">Resume</Label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {resumeFile ? resumeFile.name : "Upload Resume"}
                    </Button>
                    <span className="text-xs text-muted-foreground">PDF or Word (max 10MB)</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cover_letter">Cover Letter / Why are you interested?</Label>
                  <Textarea
                    id="cover_letter"
                    rows={4}
                    placeholder="Tell us why you're interested in this role and what you'd bring to the team..."
                    value={formData.cover_letter}
                    onChange={(e) => setFormData(prev => ({ ...prev, cover_letter: e.target.value }))}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setApplicationOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} TheRentPayer. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
